/**
 * Fashion editorial v2 — homepage inspired by premium Shopify fashion themes.
 * Fully isolated: no spreads from other templates, unique block ids (`fsh-edt-*`).
 * Preview catalog: `fashion` (see PREVIEW_TEMPLATE_CATALOG_MAP `fashionv2` → `fashion`).
 */
import type { LayoutBlock, LayoutConfig } from '@/storefront-runtime/types'

export type FashionEditorialTemplateExport = {
  id: string
  name: string
  description: string
  niche: string
  thumbnail: string
  layout: LayoutConfig
}

export const fashionEditorialHomeBlocks: LayoutBlock[] = [
  {
    id: 'fsh-edt-announce',
    type: 'announcement-bar',
    visible: true,
    props: {
      text: 'Livraison suivie · Retours 14 jours · Nouveautés chaque semaine',
      link: '/shop',
      backgroundColor: '#0a0a0a',
      textColor: '#fafaf9',
    },
  },
  {
    id: 'fsh-edt-hero-collage',
    type: 'hero-collage',
    visible: true,
    props: {
      sectionTitle: '',
      sectionSubtitle: '',
      columns: '2',
      panels: [
        {
          image: '/templates/fashion-v2-gen/fv2-gen-collage-a.webp',
          imageAlt: 'Silhouette en studio',
          badge: '3 pièces',
          overlayTitle: 'Shop the look',
          overlaySerif: 'Jusqu’à −50 %',
          overlaySubtitle: '',
          ctaLabel: '',
          ctaLink: '',
          bagLink: '/shop',
        },
        {
          image: '/templates/fashion-v2-gen/fv2-gen-collage-b.webp',
          imageAlt: 'Campagne duo',
          badge: '',
          overlayTitle: 'Soldes',
          overlaySerif: 'Jusqu’à 50 %',
          overlaySubtitle: 'Couleurs affirmées, maille travaillée.',
          ctaLabel: 'Découvrir',
          ctaLink: '/category/soldes',
          bagLink: '/shop',
        },
      ],
    },
  },
  {
    id: 'fsh-edt-stories',
    type: 'story-circles',
    visible: true,
    props: {
      title: '',
      subtitle: '',
      ringVariant: 'gold',
      items: [
        { image: '/templates/fashion-v2-gen/fv2-gen-story-01.webp', label: 'Lancement', link: '/category/nouveautes' },
        { image: '/templates/fashion-v2-gen/fv2-gen-story-02.webp', label: 'Maille', link: '/category/femme' },
        { image: '/templates/fashion-v2-gen/fv2-gen-story-03.webp', label: 'Homme', link: '/category/homme' },
        { image: '/templates/fashion-v2-gen/fv2-gen-story-04.webp', label: 'Soirée', link: '/category/soldes' },
        { image: '/templates/fashion-v2-gen/fv2-gen-story-05.webp', label: 'Studio', link: '/about' },
        { image: '/templates/fashion-v2-gen/fv2-gen-story-06.webp', label: 'Essentiels', link: '/category/accessoires' },
        { image: '/templates/fashion-v2-gen/fv2-gen-story-07.webp', label: 'Soldes', link: '/category/soldes' },
        { image: '/templates/fashion-v2-gen/fv2-gen-story-08.webp', label: 'Journal', link: '/faq' },
      ],
    },
  },
  {
    id: 'fsh-edt-spacer-1',
    type: 'spacer',
    visible: true,
    props: { size: 'md' },
  },
  {
    id: 'fsh-edt-strip',
    type: 'category-strip',
    visible: true,
    props: {},
  },
  {
    id: 'fsh-edt-split-hero',
    type: 'image-with-text',
    visible: true,
    props: {
      image: '/templates/fashion-v2-gen/fv2-gen-split-nude.webp',
      imageAlt: 'Collection nude',
      title: 'Le monde en nude et gris',
      text: 'Une collection aux couleurs douces — pensée pour se superposer à toute votre garde-robe.',
      ctaLabel: 'Robe aérienne',
      ctaLink: '/category/femme',
      layout: 'image-right',
      imageAspect: 'portrait',
    },
  },
  {
    id: 'fsh-edt-newin',
    type: 'featured-collection',
    visible: true,
    props: {
      title: 'Nouveautés',
      subtitle: 'Couleurs durables',
      collectionId: 'nouveautes',
      limit: 8,
      columns: 4,
      viewAllLabel: 'Tout voir',
      viewAllLink: '/shop',
      showQuickAdd: false,
      showPrice: true,
      showRating: true,
    },
  },
  {
    id: 'fsh-edt-last-ones',
    type: 'product-carousel',
    visible: true,
    props: {
      title: 'Dernières pièces',
      subtitle: 'Quantités limitées — swipez sur mobile.',
      limit: 8,
      autoplay: false,
      showArrows: true,
      variant: 'default',
      sectionWidth: 'full',
      ctaLabel: '',
      ctaLink: '',
      showQuickAdd: false,
      showPrice: true,
      showRating: true,
      columnsMobile: 2,
      columnsDesktop: 4,
    },
  },
  {
    id: 'fsh-edt-promo',
    type: 'promo-banner',
    visible: true,
    props: {
      text: 'Soldes studio — pièces sélectionnées jusqu’à −40 %',
      highlight: 'Stock limité',
      backgroundColor: '#b91c1c',
      textColor: '#ffffff',
      ctaLabel: 'Voir les soldes',
      ctaLink: '/category/soldes',
      showIcon: true,
    },
  },
  {
    id: 'fsh-edt-blog',
    type: 'blog-grid',
    visible: true,
    props: {
      title: 'Fashion',
      subtitle: 'Coulisses, matières et silhouettes.',
      columns: 3,
      posts: [
        {
          title: 'Explorer le coton bio',
          image: '/templates/fashion-v2-gen/fv2-gen-blog-cotton.webp',
          excerpt: 'De la fibre à la maille — pourquoi nous sourçons certifié.',
          link: '/about',
        },
        {
          title: 'Renaissance mode au Portugal',
          image: '/templates/fashion-v2-gen/fv2-gen-blog-tailoring.webp',
          excerpt: 'Ateliers partenaires, tombés précis, finitions nettes.',
          link: '/about',
        },
        {
          title: 'Élégance décontractée',
          image: '/templates/fashion-v2-gen/fv2-gen-blog-casual.webp',
          excerpt: 'Trois tenues du matin au soir avec les mêmes basiques.',
          link: '/shop',
        },
      ],
    },
  },
  {
    id: 'fsh-edt-newsletter',
    type: 'newsletter',
    visible: true,
    props: {
      title: 'Inscrivez-vous à nos e-mails',
      subtitle: 'Faites partie du voyage',
      placeholder: 'E-mail',
      buttonText: 'S’abonner',
      backgroundColor: '#fafafa',
      backgroundImage: '',
      overlayOpacity: 50,
    },
  },
  {
    id: 'fsh-edt-trust',
    type: 'trust-strip',
    visible: true,
    props: {},
  },
]

const fashionEditorialLayout: LayoutConfig = {
  schemaVersion: 1,
  theme: {
    id: 'luxury',
    primary: '#0a0a0a',
    secondary: '#f5f5f4',
    backgroundColor: '#ffffff',
    font: 'Inter',
    radius: '0.125rem',
    ui: {
      productCard: 'luxury',
      buttons: 'sharp',
      spacing: 'airy',
      headingStyle: 'editorial',
      font: 'sans',
      density: 'comfortable',
      mode: 'light',
      radius: 'md',
      /** Shop + grids: hover quick add + quick view when blocks enable it */
      productCardQuickAdd: true,
    },
  } as any,
  header: {
    props: {
      storeName: null,
      menuItems: [
        { label: 'Accueil', link: '/' },
        { label: 'Looks', link: '/shop' },
        { label: 'Nouveautés', link: '/category/nouveautes' },
        { label: 'Boutique', link: '/shop' },
        { label: 'Soldes', link: '/category/soldes' },
        { label: 'À propos', link: '/about' },
        { label: 'FAQ', link: '/faq' },
        { label: 'Contact', link: '/contact' },
      ],
      showSearch: true,
      showCart: true,
      sticky: true,
      variant: 'minimal',
      promoText: null,
      promoColor: null,
    },
  },
  footer: {
    props: {
      storeName: true,
      description:
        'Mode éditoriale — pièces sélectionnées, matières nobles, service client réactif. Chaque commande est préparée avec soin.',
      copyright: `© ${new Date().getFullYear()} Boutique. Tous droits réservés.`,
      columns: [
        {
          title: 'Service client',
          links: [
            { label: 'Livraison', link: '/policy/shipping' },
            { label: 'Retours', link: '/policy/returns' },
            { label: 'FAQ', link: '/faq' },
          ],
        },
        {
          title: 'Collections',
          links: [
            { label: 'Nouveautés', link: '/category/nouveautes' },
            { label: 'Femme', link: '/category/femme' },
            { label: 'Homme', link: '/category/homme' },
            { label: 'Accessoires', link: '/category/accessoires' },
            { label: 'Soldes', link: '/category/soldes' },
          ],
        },
        {
          title: 'Informations',
          links: [
            { label: 'Contact', link: '/contact' },
            { label: 'À propos', link: '/about' },
          ],
        },
      ],
      socialLinks: {
        instagram: 'https://instagram.com',
        facebook: 'https://facebook.com',
      },
      showPaymentIcons: true,
    },
  },
  pages: {
    home: fashionEditorialHomeBlocks,
    shop: [
      {
        id: 'fsh-edt-sh-announce',
        type: 'announcement-bar',
        visible: true,
        props: { text: 'Filtres synchronisés · Aperçu rapide', link: '', backgroundColor: '#fafaf9', textColor: '#0a0a0a' },
      },
      {
        id: 'fsh-edt-sh-qv',
        type: 'quick-view',
        visible: true,
        props: { enabled: true, layout: 'advanced' },
      },
      {
        id: 'fsh-edt-sh-hero',
        type: 'hero',
        visible: true,
        props: {
          headline: 'Boutique',
          subtitle: 'Femme, homme et accessoires — sélection éditoriale.',
          ctaLabel: 'Parcourir',
          anchorId: 'fsh-edt-grid',
          backgroundImage: '/templates/fashion-v2-gen/fv2-gen-shop-hero.webp',
          layout: 'centered',
          overlayOpacity: 40,
          textColor: '#ffffff',
          textAlign: 'center',
          minHeight: 'md',
        },
      },
      {
        id: 'fsh-edt-sh-feat',
        type: 'featured-collection',
        visible: true,
        props: {
          title: 'Sélection',
          subtitle: '',
          collectionId: '',
          limit: 6,
          columns: 4,
          viewAllLabel: '',
          viewAllLink: '',
          showQuickAdd: true,
          showPrice: true,
          showRating: true,
        },
      },
      {
        id: 'fsh-edt-sh-grid',
        type: 'product-grid',
        visible: true,
        props: {
          title: 'Tous les articles',
          subtitle: '',
          limit: 24,
          columns: 4,
          anchorId: 'fsh-edt-grid',
          showQuickAddOnCards: true,
        },
      },
    ],
    product: [
      {
        id: 'fsh-edt-pd-details',
        type: 'product-details',
        visible: true,
        props: {
          highlights: ['Coupe travaillée', 'Matières sélectionnées', 'Finitions nettes'],
          trustShipping: 'Livraison suivie sur tout le territoire.',
          trustReturns: 'Retours 14 jours — pièces non portées avec étiquettes.',
          trustPayment: 'Paiement à la livraison et cartes — total confirmé avant débit.',
        },
      },
      {
        id: 'fsh-edt-pd-tabs',
        type: 'product-tabs',
        visible: true,
        props: {
          tabs: [
            { label: 'Description', content: 'Pièce conçue pour une tenue jour et soir — détails de coupe et entretien sur l’étiquette.' },
            { label: 'Matière', content: 'Composition et conseils d’entretien disponibles sur la fiche article.' },
            { label: 'Livraison', content: 'Expédition sous 24–48h ouvrables.' },
          ],
        },
      },
      {
        id: 'fsh-edt-pd-related',
        type: 'related-products',
        visible: true,
        props: {
          title: 'Vous aimerez aussi',
          limit: 8,
          columns: 4,
          showQuickAdd: false,
          showPrice: true,
          showRating: true,
        },
      },
    ],
    category: [
      {
        id: 'fsh-edt-ct-announce',
        type: 'announcement-bar',
        visible: true,
        props: { text: 'Retours 14 jours · Conseil taille sur demande', link: '/contact', backgroundColor: '#0a0a0a', textColor: '#fafaf9' },
      },
      {
        id: 'fsh-edt-ct-banner',
        type: 'image-banner',
        visible: true,
        props: {
          image: '/templates/fashion-v2-gen/fv2-gen-cat-banner.webp',
          title: 'La collection',
          subtitle: 'Textures, tombés et styling.',
          ctaLabel: 'Toute la boutique',
          ctaLink: '/shop',
          overlayOpacity: 34,
          minHeight: 'md',
          textAlign: 'left',
        },
      },
      { id: 'fsh-edt-ct-strip', type: 'category-strip', visible: true, props: {} },
      { id: 'fsh-edt-ct-grid', type: 'category-grid', visible: true, props: {} },
    ],
    cart: [
      { id: 'fsh-edt-cart-lines', type: 'cart-lines', visible: true, props: {} },
      { id: 'fsh-edt-cart-sum', type: 'cart-summary', visible: true, props: {} },
    ],
    checkout: [{ id: 'fsh-edt-co-form', type: 'checkout-form', visible: true, props: {} }],
    faq: [
      {
        id: 'fsh-edt-faq',
        type: 'faq-accordion',
        visible: true,
        props: {
          title: 'Questions fréquentes',
          subtitle: 'Commandes, retours, guide des tailles',
          items: [
            { question: 'Comment choisir ma taille ?', answer: 'Consultez le guide sur chaque fiche ou écrivez-nous avec vos mesures.' },
            { question: 'Délai de livraison ?', answer: 'En général 24 à 48h ouvrables après validation du paiement.' },
          ],
        },
      },
    ],
    about: [
      {
        id: 'fsh-edt-ab-banner',
        type: 'image-banner',
        visible: true,
        props: {
          image: '/templates/fashion-v2-gen/fv2-gen-about-vision.webp',
          title: 'Notre vision',
          subtitle: 'Moins mais mieux — pièces durables et silhouettes affirmées.',
          ctaLabel: 'Découvrir la boutique',
          ctaLink: '/shop',
          overlayOpacity: 30,
          minHeight: 'lg',
          textAlign: 'center',
        },
      },
      {
        id: 'fsh-edt-ab-text',
        type: 'rich-text',
        visible: true,
        props: {
          title: 'Maison éditoriale',
          text: 'Nous concevons des collections limitées avec des partenaires choisis pour la qualité de coupe et de matière.\n\nChaque lancement est pensé comme un chapitre — avec des visuels studio et des repères clairs pour vous aider à composer vos tenues.',
        },
      },
    ],
    contact: [
      {
        id: 'fsh-edt-contact',
        type: 'rich-text',
        visible: true,
        props: {
          title: 'Contact',
          text: 'Email : contact@votreboutique.tn\nWhatsApp : +216 XX XXX XXX\nHoraires : 10h–20h',
        },
      },
    ],
  },
}

export const fashionEditorialTemplate: FashionEditorialTemplateExport = {
  id: 'fashionv2',
  name: 'Fashion Editorial (v2)',
  description:
    'Accueil type thème Shopify mode : story circles, hero collage multi-colonnes, nouveautés en vedette, carrousel, journal et newsletter.',
  niche: 'Mode',
  thumbnail: '/templates/fashion-v2-gen/fv2-gen-thumb.webp',
  layout: fashionEditorialLayout,
}
